/**
 * This module is a wrapper for jwplayer instances
 *
 * @param {Window} window    jwplayer instance will be appended to this context
 * @param {Object} config    Contains config params
 * @param {Object} outstream PhoenixAdOutstream interface
 *
 * API:
 * - on          {Function}
 * - off         {Function}
 * - isLibLoaded {Function}
 * - isSetup     {Function}
 * - play        {Function}
 * - pause       {Function}
 * - seek        {Function}
 * - destroy     {Function}
 */
let Player = function (window, config, outstream) {
    const document = window.document;

    /**
     * Loads jwplayer instance
     */
    function Constructor() {
        let promise = loadPlayerPromise();
        promise
            .then(() => {
                isLibLoaded = true;
                outstream.libReady();
            })
            .catch((err) => {
                outstream.libLoadError(err);
            });
    }

    // local vars
    let player;
    let isLibLoaded = false;
    let api = Constructor.prototype;
    let muteToggle;
    let unmuteIcon;
    let muteIcon;
    let muted = true;
    let didEnd = false;

    /**
     * Sets up the jwplayer instance.
     * @param {String} vastUrl The url of the tag to load in the player.
     * @param {String} playerBiddingIds ids for exchange bidding.
     * @param {DOMElement} unmuteToggleElement The unmute/mute button.
     */
    api.setup = function (vastUrl, playerBiddingIds, unmuteToggleElement) {
        if (player) {
            return;
        }

        let container = document.querySelector('.m-ad-outstream--player-container'),
            playerConfig = {
                file: config.fileUrl,
                repeat: false,
                mute: true,
                width: '100%',
                aspectratio: '16:9',
                skin: {
                    name: 'ad-outstream-say',
                },
                advertising: {
                    autoplayadsmuted: true,
                    client: 'googima',
                    requestTimeout: 15000,
                    schedule: {
                        adbreak: {
                            tag: vastUrl,
                            offset: 'pre',
                        },
                    },
                    vpaidcontrols: true,
                },
            };
        if (playerBiddingIds) {
            try {
                playerBiddingIds = JSON.parse(playerBiddingIds);
                playerConfig.advertising.bids = {
                    settings: { mediationLayerAdServer: 'dfp' },
                    bidders: playerBiddingIds,
                };
            } catch (e) {
                playerBiddingIds = undefined;
            }
        }
        // instantiate player
        player = window.jwplayer(container);
        player.setup(playerConfig);

        muteToggle = unmuteToggleElement;
        unmuteIcon = unmuteToggleElement.querySelector('.m-ad-outstream--unmute-icon');
        muteIcon = unmuteToggleElement.querySelector('.m-ad-outstream--mute-icon');
        // Ensure the mute toggle displays the appropriate icon to start
        muteIcon.style.display = 'none';
        addListeners();
    };

    /**
     * Constructor
     * returns a Promise that loads the jwplayer lib
     * @return {Promise}
     */
    function loadPlayerPromise() {
        return new Promise((resolve, reject) => {
            let tag = document.createElement('script');
            tag.addEventListener('load', () => {
                resolve();
            });
            tag.addEventListener('error', (err) => {
                reject(err);
            });
            tag.src = config.scriptSrc;
            document.head.appendChild(tag);
        });
    }

    function addListeners() {
        muteToggle.addEventListener('click', muteToggleHandler);
        player.on('adClick', clickoutHandler);
    }

    function removeListeners() {
        muteToggle.removeEventListener('click', muteToggleHandler);
        if (player) {
            player.off('adClick', clickoutHandler);
        }
        window.removeEventListener('focus', refocusHandler);
    }

    /**
     * Handler for click out.
     * If the user clicks out of the ad while it is still playing, jwPlayer pauses the ad
     * and the destination url opens in a new tab.
     * Because we do not expose jwPlayer controls for these ads, if the user returns to this page,
     * the ad is stuck in a paused state.
     * We listen for when this tab gets focus again to resume playback.
     */
    function clickoutHandler() {
        window.addEventListener('focus', refocusHandler);
    }

    /**
     * We assume the ad has not moved on the page since when the user clicked out,
     * and so refocus will resume playback.
     * The only condition we check here is to ensure that the clickoutHandler was not triggered
     * from the endcard.
     */
    function refocusHandler() {
        window.removeEventListener('focus', refocusHandler);
        if (!didEnd) {
            player.play();
        }
    }

    function muteToggleHandler(e) {
        e.stopPropagation();
        e.preventDefault();
        muted = !muted;
        api.setMute(muted);
        if (muted) {
            muteIcon.style.display = 'none';
            unmuteIcon.style.display = 'block';
        } else {
            muteIcon.style.display = 'block';
            unmuteIcon.style.display = 'none';
        }
    }

    api.ended = function () {
        didEnd = true;
    };

    /**
     * Wraps around the jwplayer instance 'on' method which binds
     * an event listener to the player instance
     * @param  {String}   name     Event name
     * @param  {Function} callback
     */
    api.on = function (name, callback) {
        player.on(name, callback);
    };

    /**
     * Wraps around the jwplayer instance 'off' method which unbinds
     * an event listener from the player instance
     * @param  {String}   name     Event name
     * @param  {Function} callback
     */
    api.off = function (name, callback) {
        if (player) {
            player.off(name, callback);
        }
    };

    api.isLibLoaded = function () {
        return isLibLoaded;
    };

    api.pause = function () {
        player.pause();
    };

    api.play = function () {
        player.play();
    };

    api.seek = function (position) {
        player.seek(position);
    };

    api.getState = function () {
        return player.getState();
    };

    /**
     * Set the mute state of the player.
     * If the state is undefined, mute is toggled.
     */
    api.setMute = function (state) {
        player.setMute(state);
    };

    api.destroy = function () {
        removeListeners();
        player.remove();
        player = null;
    };

    return new Constructor();
};

module.exports = Player;
